// Renders the host's modal stack. The host owns the stack state (so actions
// can close all / pop top etc.); this component walks it in order and
// renders the matching ModalDef from the registry. Unknown ids log a warning
// and skip — see .design.md "Versioning and migration".
//
// Per-render `renderOverrides` lets the host supply renderers for ids whose
// content depends on live scene state (cart, email-form), while the registry
// metadata (size, dismiss flags) stays authoritative.

import { getModal } from "./registry";
import type { ModalContext, ModalRenderOverrides, ModalStack } from "./types";

export interface ActiveModalRendererProps {
  stack: ModalStack;
  /** Close a specific id (pop from anywhere in the stack — typically the top). */
  closeOne: (id: string) => void;
  closeAll: () => void;
  /** Per-render renderer overrides. Wins over the registered render. */
  renderOverrides?: ModalRenderOverrides;
}

export function ActiveModalRenderer({
  stack,
  closeOne,
  closeAll,
  renderOverrides,
}: ActiveModalRendererProps) {
  if (stack.length === 0) return null;

  return (
    <>
      {stack.map(id => {
        const def = getModal(id);
        if (!def) {
          console.warn(`[SceneDesigner] modal id "${id}" not in registry; skipping.`);
          return null;
        }
        const render = renderOverrides?.[id] ?? def.render;
        if (!render) {
          console.warn(`[SceneDesigner] modal "${id}" has no render; skipping.`);
          return null;
        }
        const ctx: ModalContext = {
          close: () => closeOne(id),
          closeAll,
        };
        const dismissOnBackdrop = def.dismissOnBackdrop ?? true;
        return (
          <div
            key={id}
            className={"sd-modal-backdrop sd-modal-backdrop--" + def.size}
            data-modal-id={id}
            onClick={dismissOnBackdrop ? () => closeOne(id) : undefined}
          >
            <div className="sd-modal" onClick={e => e.stopPropagation()}>
              {render(ctx)}
            </div>
          </div>
        );
      })}
    </>
  );
}
